import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { IMAGES } from "@/lib/content";
import { useLang } from "@/lib/LanguageContext";

const EASE = [0.22, 1, 0.36, 1];

export default function Hero() {
  const { c, lang } = useLang();
  const hero = c.hero;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.12, 1.3]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-[100svh] flex items-center overflow-hidden pt-[100px] sm:pt-[128px] lg:pt-[160px] pb-10">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#12501f] via-[#2f8a43] to-[#1a5e2b]" />
        <img src={IMAGES.farm} alt="Venezuelan plantain plantation" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0c3317] via-[#0c3317]/30 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0c3317]/80 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_30%,rgba(92,148,34,0.38),transparent_55%)]" />
        <img src="/leaves-corner.png" alt="" aria-hidden="true" style={{ mixBlendMode: "screen" }} className="pointer-events-none absolute top-0 right-0 w-[460px] lg:w-[560px] -mt-16 -mr-20 rotate-6 opacity-70" />
        <img src="/leaves-side.png" alt="" aria-hidden="true" style={{ mixBlendMode: "screen" }} className="pointer-events-none absolute bottom-0 left-0 h-[440px] lg:h-[560px] -ml-16 -mb-6 -rotate-12 opacity-50 hidden md:block" />
      </motion.div>

      <motion.div
        aria-hidden
        initial={{ opacity: 0, scaleX: 0.6 }}
        animate={{ opacity: 1, scaleX: 1 }}
        transition={{ duration: 1.6, ease: EASE, delay: 0.4 }}
        className="pointer-events-none absolute left-1/2 top-[14%] -translate-x-1/2 w-[78vw] max-w-[900px] h-[42vh] border-t border-x border-gold/25 arch-top origin-top"
      />

      <motion.div style={{ y: contentY, opacity: fade }} className="relative z-10 w-full mx-auto max-w-[1400px] px-5 sm:px-8">
        <motion.div
          key={`eyebrow-${lang}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: EASE, delay: 0.2 }}
          className="flex items-center gap-3 text-gold mb-7"
        >
          <span className="h-px w-10 bg-gold/60" />
          <span className="font-sans text-[10px] sm:text-xs tracking-[0.28em] uppercase text-gold/90">{hero.eyebrow}</span>
        </motion.div>

        <h1 className="font-serif font-light tracking-[-0.02em] leading-[0.92] text-cream text-[15vw] sm:text-[11vw] lg:text-[8.2vw]">
          {hero.lines.map((line, i) => (
            <span key={`${lang}-${i}`} className="block overflow-hidden pb-[0.14em]">
              <motion.span
                className="block"
                initial={{ y: "115%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.1, ease: EASE, delay: 0.3 + i * 0.14 }}
              >
                {i === 1 ? <span className="text-gold italic">{line}</span> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-9 flex flex-col lg:flex-row lg:items-end gap-8 lg:gap-16">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE, delay: 0.75 }}
            className="max-w-xl font-sans text-sm sm:text-base leading-relaxed text-cream/70"
          >
            {hero.paragraph}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE, delay: 0.9 }}
            className="flex flex-wrap items-center gap-3 sm:gap-4"
          >
            <a href="#products" data-testid="hero-explore-btn" className="group inline-flex items-center gap-2 bg-gold text-forest font-sans text-xs uppercase tracking-[0.18em] px-7 py-4 hover:bg-cream transition-colors duration-400">
              {hero.cta1}
              <ArrowDownRight size={16} className="transition-transform duration-400 group-hover:translate-x-1 group-hover:translate-y-1" />
            </a>
            <a href="#contact" data-testid="hero-contact-btn" className="inline-flex items-center border border-cream/30 text-cream font-sans text-xs uppercase tracking-[0.18em] px-7 py-4 hover:border-gold hover:text-gold transition-colors duration-400">
              {hero.cta2}
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, ease: EASE, delay: 1.1 }}
          className="mt-14 pt-8 border-t border-gold/20 flex items-center gap-5"
        >
          <span className="font-serif text-5xl sm:text-6xl text-gold leading-none">{hero.experience.value}</span>
          <span className="font-sans text-[11px] sm:text-sm uppercase tracking-[0.2em] text-cream/60 max-w-[240px] leading-relaxed">
            {hero.experience.label}
          </span>
        </motion.div>
      </motion.div>
    </section>
  );
}
